import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  Signal,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ManagePatientButtonComponent } from '@principle-theorem/ng-patient';
import {
  ContactSelectorComponent,
  OrganisationService,
  PatientSelectorComponent,
  PracticeSelectorComponent,
  PractitionerSelectorComponent,
} from '@principle-theorem/ng-principle-shared';
import {
  NgCommonModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  InitReferralData,
  Patient,
  Referral,
  normaliseEmail,
} from '@principle-theorem/principle-core';
import {
  IBrand,
  IContact,
  IContactNumber,
  IPatient,
  IPractice,
  IReferral,
  IStaffer,
  ReferralParticipant,
  ReferralRole,
  ReferralStatus,
  ReferralType,
  ReferralUrgency,
  isContact,
  isPatient,
  isStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  WithRef,
  filterUndefined,
  getEnumValues,
  isSameRef,
  isWithRef,
  toNamedDocument,
} from '@principle-theorem/shared';
import { combineLatest } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

type ReferralFormData = Pick<IReferral, 'type' | 'urgency'>;
interface IFormState extends ReferralFormData {
  referrer: ReferralParticipant | undefined;
  recipient: ReferralParticipant | undefined;
  practice: WithRef<IPractice> | undefined;
  patient: WithRef<IPatient> | undefined;
  externalUserEmail: string | undefined;
}

@Component({
  selector: 'pr-referral-form',
  imports: [
    NgCommonModule,
    MatButtonToggleModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    PractitionerSelectorComponent,
    ManagePatientButtonComponent,
    ContactSelectorComponent,
    PracticeSelectorComponent,
    PatientSelectorComponent,
  ],
  templateUrl: './referral-form.component.html',
  styleUrl: './referral-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralFormComponent {
  private _organisation = inject(OrganisationService);
  private _contactNumbers = signal<IContactNumber[]>([]);
  hasMultiplePractices = toSignal(this._organisation.hasMultiplePractices$);
  practice = input.required<WithRef<IPractice>>();
  brand = input.required<WithRef<IBrand>>();
  staffer = input.required<WithRef<IStaffer>>();
  referral = input<WithRef<IReferral> | undefined>(undefined);
  recipient = input<ReferralParticipant | undefined>(undefined);
  referrer = input<ReferralParticipant | undefined>(undefined);
  patient = input<WithRef<IPatient> | undefined>(undefined);
  isCreate = input(true, { transform: coerceBooleanProperty });
  isDisabled = computed(() => !this.isCreate());
  referralChange = output<InitReferralData | undefined>();
  patientChange = output<WithRef<IPatient>>();
  patientCtrl = new TypedFormControl<WithRef<IPatient> | string>();
  formState = signal<IFormState>({
    referrer: undefined,
    recipient: undefined,
    practice: undefined,
    patient: undefined,
    type: ReferralType.Outgoing,
    urgency: ReferralUrgency.Routine,
    externalUserEmail: undefined,
  });

  readonly referralType = ReferralType;
  readonly referralTypes = getEnumValues(ReferralType);
  readonly referralStatuses = getEnumValues(ReferralStatus);
  readonly referralUrgencies = getEnumValues(ReferralUrgency);

  outgoingReferrer = this._createTypedComputed(
    ReferralType.Outgoing,
    ReferralRole.Referrer,
    isStaffer
  );

  outgoingRecipient = this._createTypedComputed<WithRef<IContact>>(
    ReferralType.Outgoing,
    ReferralRole.Recipient,
    (value): value is WithRef<IContact> => isWithRef(value) && isContact(value)
  );

  incomingReferrer = this._createTypedComputed(
    ReferralType.Incoming,
    ReferralRole.Referrer,
    (value): value is WithRef<IContact> => isWithRef(value) && isContact(value)
  );

  incomingRecipient = this._createTypedComputed(
    ReferralType.Incoming,
    ReferralRole.Recipient,
    isStaffer
  );

  form = new TypedFormGroup<ReferralFormData>({
    type: new TypedFormControl<ReferralType>(ReferralType.Outgoing),
    urgency: new TypedFormControl<ReferralUrgency>(ReferralUrgency.Routine),
  });

  get typeValue(): ReferralType {
    return this.form.controls.type.value || ReferralType.Outgoing;
  }

  constructor() {
    this._setPatient();
    this._setPractice();
    this._setDefaultPractitioner();
    this._handleFormChanges();
    this._handlePatientChanges();
    this._handleReferralTypeChanges();
    this._handleReferralDataChanges();
    this._setReferral();
  }

  updateChanges(changes: Partial<IFormState>): void {
    this.formState.update((current) => ({
      ...current,
      ...changes,
    }));
  }

  setReferrerControl(referrer?: ReferralParticipant): void {
    this.updateChanges({ referrer });
    this._addExternalUserEmail(referrer);
  }

  setRecipientControl(recipient?: ReferralParticipant): void {
    this.updateChanges({ recipient });
    this._addExternalUserEmail(recipient);
  }

  setPracticeControl(practice?: WithRef<IPractice>): void {
    this.updateChanges({ practice });
  }

  updatePatient(patient: WithRef<IPatient>): void {
    this.patientChange.emit(patient);
    this.patientCtrl.patchValue(patient, { emitEvent: false });
  }

  private _addExternalUserEmail(participant?: ReferralParticipant): void {
    if (!isContact(participant)) {
      return;
    }
    if (!participant.email) {
      return;
    }
    const externalUserEmail = normaliseEmail(participant.email);
    this.updateChanges({ externalUserEmail });
  }

  private _getStaffer(
    referrer: ReferralParticipant,
    recipient: ReferralParticipant
  ): WithRef<IStaffer> {
    const staffer = isStaffer(referrer) ? referrer : recipient;
    return staffer as WithRef<IStaffer>;
  }

  private _setDefaultPractitioner(): void {
    if (!this.isCreate()) {
      return;
    }
    combineLatest([
      toObservable(this.staffer),
      this._organisation.practicePractitioners$.pipe(filterUndefined()),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([staffer, practitioners]) => {
        const selected = practitioners.find((matchedPractitioner) =>
          isSameRef(matchedPractitioner, staffer)
        );
        this.formState.update((current) => ({
          ...current,
          referrer: selected,
        }));
      });
  }

  private _setReferral(): void {
    combineLatest([
      toObservable(this.referrer).pipe(filterUndefined()),
      toObservable(this.recipient).pipe(filterUndefined()),
      toObservable(this.referral).pipe(filterUndefined()),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([referrer, recipient, referral]) => {
        this.form.patchValue(referral, { emitEvent: false });

        this.updateChanges({
          referrer,
          recipient,
          externalUserEmail: referral.externalUserEmail,
        });
        this.form.disable();
        this.patientCtrl.disable();
      });
  }

  private _setPatient(): void {
    toObservable(this.patient)
      .pipe(filterUndefined(), takeUntilDestroyed())
      .subscribe((patient) => this.patientCtrl.setValue(patient));
  }

  private _setPractice(): void {
    toObservable(this.practice)
      .pipe(takeUntilDestroyed(), filterUndefined())
      .subscribe((practice) => this.updateChanges({ practice }));
  }

  private _handleFormChanges(): void {
    this.form.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((referral) => this.updateChanges(referral));
  }

  private _handlePatientChanges(): void {
    this.patientCtrl.valueChanges
      .pipe(
        filter((patient) => isWithRef(patient) && isPatient(patient)),
        switchMap(async (patient) => ({
          patient,
          contactNumbers: await Patient.resolveContactNumbers(patient),
        })),
        takeUntilDestroyed()
      )
      .subscribe(({ patient, contactNumbers }) => {
        this._contactNumbers.set(contactNumbers);
        this.updateChanges({ patient });
      });
  }

  private _handleReferralTypeChanges(): void {
    this.form.controls.type.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        if (!this.isCreate()) {
          return;
        }

        this.updateChanges({
          referrer: undefined,
          recipient: undefined,
        });
      });
  }

  private _handleReferralDataChanges(): void {
    toObservable(this.formState)
      .pipe(takeUntilDestroyed())
      .subscribe((state) => {
        if (!this.isCreate()) {
          return;
        }
        if (
          !(
            state.referrer &&
            state.recipient &&
            state.practice &&
            state.patient &&
            state.type &&
            state.urgency &&
            state.externalUserEmail
          )
        ) {
          this.referralChange.emit(undefined);
          return;
        }

        const staffer = this._getStaffer(state.referrer, state.recipient);
        const patient = state.patient;
        const contactNumbers = this._contactNumbers();
        const primaryContact = Referral.getPrimaryContact(patient);

        const referralData: InitReferralData = {
          referrer: Referral.participantToNamedDoc(state.referrer),
          recipient: Referral.participantToNamedDoc(state.recipient),
          participants: [state.recipient.ref, state.referrer.ref],
          practice: toNamedDocument(state.practice),
          patient: toNamedDocument(patient),
          type: state.type,
          urgency: state.urgency,
          externalUserEmail: state.externalUserEmail,
          stafferDetails: Referral.stafferDetails(staffer, state.practice),
          patientDetails: Referral.buildPatientDetails(
            patient,
            primaryContact,
            contactNumbers
          ),
        };

        this.referralChange.emit(referralData);
      });
  }

  private _createTypedComputed<T>(
    referralType: ReferralType,
    field: ReferralRole,
    typeGuard: (value: unknown) => value is T
  ): Signal<T | undefined> {
    return computed<T | undefined>(() => {
      const state = this.formState();
      if (state.type !== referralType) {
        return;
      }
      const value = state[field];
      return typeGuard(value) ? value : undefined;
    });
  }
}
